import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '../../UI';
import { Button } from '../../UI/base/Button';
import type { TableColumn } from '../../UI';
import { SectionHeader } from '../../SectionHeader/SectionHeader';
import styles from './bookings.module.css';

interface BookingWithId {
  id: string;
}

interface BookingListSectionProps<T extends BookingWithId> {
  /** i18n key prefix; resolves to `${keyPrefix}-header`, `-add`, `-loading`, `-retry`, etc. */
  keyPrefix: string;
  isLoading: boolean;
  /** Combined load error (bookings and/or the entity list); shows the retry panel when set. */
  errorMessage?: string | null;
  /** Null until a location is selected — the create button and table stay hidden without one. */
  locationId: string | null;
  columns: TableColumn<T>[];
  bookings: T[];
  minWidth: number;
  onAdd: () => void;
  onRetry: () => void;
  /** Modals (form / delete) the page renders alongside the list. */
  children?: ReactNode;
}

// The shared list scaffold every booking page renders: section header + add button, the
// loading / error+retry / no-location / empty states, and the table. Page-specific modals are
// passed as children.
export const BookingListSection = <T extends BookingWithId>({
  keyPrefix,
  isLoading,
  errorMessage,
  locationId,
  columns,
  bookings,
  minWidth,
  onAdd,
  onRetry,
  children
}: BookingListSectionProps<T>) => {
  const { t } = useTranslation();
  const hasNoLocation = !isLoading && !errorMessage && !locationId;
  const showEmptyState =
    !isLoading && !errorMessage && Boolean(locationId) && bookings.length === 0;

  return (
    <section>
      <SectionHeader title={t(`${keyPrefix}-header`)}>
        <Button type="primary" disabled={!locationId} onClick={onAdd}>
          {t(`${keyPrefix}-add`)}
        </Button>
      </SectionHeader>

      {isLoading && bookings.length === 0 && (
        <p className={styles.emptyState}>{t(`${keyPrefix}-loading`)}</p>
      )}

      {errorMessage && (
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>{errorMessage}</p>
          <Button type="secondary" onClick={onRetry}>
            {t(`${keyPrefix}-retry`)}
          </Button>
        </div>
      )}

      {hasNoLocation && <p className={styles.emptyState}>{t(`${keyPrefix}-no-location`)}</p>}

      {showEmptyState && <p className={styles.emptyState}>{t(`${keyPrefix}-empty`)}</p>}

      {bookings.length > 0 && (
        <Table
          columns={columns}
          data={bookings}
          getRowKey={(booking) => booking.id}
          minWidth={minWidth}
        />
      )}

      {children}
    </section>
  );
};
